import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useTaskEditing Hook
 * 
 * Handles all inline editing logic for task names:
 * - Start/finish editing
 * - Cursor position tracking
 * - Auto-start editing for new tasks
 * - Keyboard handling (Enter to save, Escape to cancel)
 */
export const useTaskEditing = ({
  taskId,
  taskName,
  isSubtask,
  isPlaceholderName,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(taskName);
  
  const inputRef = useRef(null);
  const nameSpanRef = useRef(null);
  const clickCursorPosRef = useRef(null);
  const autoStartedForTaskRef = useRef(null);

  // Start editing handler
  const handleStartEdit = useCallback((e) => {
    if (isSubtask) return;
    e.stopPropagation();
    
    // If clicking on placeholder name, clear it and start fresh
    if (isPlaceholderName) {
      setEditValue('');
      clickCursorPosRef.current = null;
    } else if (nameSpanRef.current && window.getSelection) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && nameSpanRef.current.contains(range.startContainer)) {
        clickCursorPosRef.current = range.startOffset;
      } else {
        clickCursorPosRef.current = taskName.length;
      }
      setEditValue(taskName);
    } else {
      clickCursorPosRef.current = taskName.length;
      setEditValue(taskName);
    }
    setIsEditing(true);
  }, [taskName, isSubtask, isPlaceholderName]);

  // Finish editing handler
  const handleFinishEdit = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== taskName) {
      onRename?.(taskId, trimmedValue);
    }
    setIsEditing(false);
  }, [editValue, taskId, taskName, onRename]);

  // Keyboard handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFinishEdit();
    } else if (e.key === 'Escape') {
      setEditValue(taskName);
      setIsEditing(false);
    }
  }, [handleFinishEdit, taskName]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing) {
      const rafId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          if (clickCursorPosRef.current !== null) {
            const pos = clickCursorPosRef.current;
            inputRef.current.setSelectionRange(pos, pos);
          } else {
            inputRef.current.setSelectionRange(len, len);
          }
          clickCursorPosRef.current = null;
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isEditing]);

  // Auto-start editing when task has default placeholder name (newly created)
  useEffect(() => {
    if (isSubtask) return;
    if (isPlaceholderName && !isEditing && autoStartedForTaskRef.current !== taskId) {
      autoStartedForTaskRef.current = taskId;
      setEditValue('');
      setIsEditing(true);
    }
  }, [taskId, isPlaceholderName, isEditing, isSubtask]);

  return {
    isEditing,
    editValue,
    setEditValue,
    inputRef,
    nameSpanRef,
    handleStartEdit,
    handleFinishEdit,
    handleKeyDown,
  };
};

export default useTaskEditing;
